import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { WorkflowForm, WorkflowFormComponent, WorkflowFormKey, WorkflowTaskDataService } from '@lib/workflow-integration';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    IPurchaseRequisitionTaskPreview,
    PurchaseRequisitionTaskPreview,
} from '../../types/PurchaseRequisitionTaskPreview';
import { DemandDataService } from '../../../demand/demand-data.service';
import { CommentDataService } from '@lib/comment';
import { ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import {
    createDeeplinkCompletionContext,
    CustomRouter,
    DeeplinkCompletionAction,
    DeeplinkCompletionContextService,
    NotificationMessageLevel,
    NotificationService,
} from '@lib/core';
import { ActivatedRoute } from '@angular/router';
import {
    IPurchaseRequisitionApproveWithResultSelectionTask,
    PurchaseRequisitionApproveWithResultSelectionTask,
    PurchaseRequisitionResultOption,
} from './types/PurchaseRequisitionApproveWithResultSelectionTask';
import { PurchaseRequisitionApproveWithResultSelectionCompletionData } from './types/PurchaseRequisitionApproveWithResultSelectionCompletionData';
import { FormControl } from '@angular/forms';
import { PurchaseRequisitionResult } from '../../../purchase-requisition/types/PurchaseRequisitionResult';
import { takeUntil } from 'rxjs/operators';
import { ExplanationDialogService, IExplanationDialogResult } from '@lib/util';
import { I18nService } from '@lib/i18n';
import { HttpErrorResponse } from '@angular/common/http';
import { DemandSelectableCurrencies } from '../../../demand/types/IDemandSelectableCurrencies';
import { DemandValidationError, IDemandValidationError } from '../../../demand/validation/types/IDemandValidationError';
import { IPurchaseRequisitionValidationError } from '../../../demand/validation/types/IPurchaseRequisitionValidationError';
import { IValidationError, ValidationSubject } from '../../../demand/validation/types/IValidationError';
import {
    AccountingValidationError,
    IAccountingValidationError,
} from '../../../demand/validation/types/IAccountingValidationError';
import { PurchaseRequisitionDataService } from '../../../purchase-requisition/purchase-requisition-data.service';
import { IPurchaseRequisitionCompanyCodeValidationResponse } from '../../../purchase-requisition/types/IPurchaseRequisitionCompanyCodeValidationResponse';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'purchase-requisition-approve-with-result-selection',
    templateUrl: './purchase-requisition-approve-with-result-selection.component.html',
    styleUrls: ['./purchase-requisition-approve-with-result-selection.component.css'],
    standalone: false,
})
@WorkflowForm(
    marker('Bedarfsmeldung freigeben'),
    [WorkflowFormKey.PURCHASE_REQUISITION_RELEASE_AND_CHOOSE_RESULT_FORM],
    (task: IPurchaseRequisitionTaskPreview, displayName: string) =>
        new PurchaseRequisitionTaskPreview(task, displayName),
    marker('Bedarfsmeldung freigeben'),
)
export class PurchaseRequisitionApproveWithResultSelectionComponent extends WorkflowFormComponent implements OnInit {
    private readonly taskDataService = inject(WorkflowTaskDataService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly commentDataService = inject(CommentDataService);
    private readonly itemClassificationDataService = inject(ItemClassificationDataService);
    private readonly fileDataService = inject(FileDataService);
    private readonly router = inject(CustomRouter);
    private readonly route = inject(ActivatedRoute);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly explanationDialog = inject(ExplanationDialogService);
    private readonly i18n = inject(I18nService);
    private readonly deeplinkCompletionContext = inject(DeeplinkCompletionContextService);
    private readonly purchaseRequisitionDataService = inject(PurchaseRequisitionDataService);
    private readonly notificationService = inject(NotificationService);

    public task?: PurchaseRequisitionApproveWithResultSelectionTask;
    public selectedResultCtrl?: FormControl<PurchaseRequisitionResultOption>;
    public purchaseRequisitionValid = false;
    /** Currency acceptance of the demand (EPROC-11425), pushed up from the embedded detail view. */
    public currencyAcceptance?: DemandSelectableCurrencies;
    private explanation = '';
    private hasCompanyCodeValidationError = false;

    public get resultValid(): boolean {
        return (
            !!this.selectedResultCtrl?.value &&
            (this.selectedResultCtrl.value?.equals(PurchaseRequisitionResult.RFQ) ||
                !!this.task?.hasOneSupplierSelected)
        );
    }

    public get rfqResultSelected(): boolean {
        return !!this.selectedResultCtrl?.value?.equals(PurchaseRequisitionResult.RFQ);
    }

    /**
     * EPROC-11425: the ONLY hard currency block. Selecting currency and suppliers stays free (hints
     * only); converting the demand into an order in a currency its suppliers do not accept is
     * gated here — mirrored by the server-side release validation.
     */
    public get currencyBlocksOrder(): boolean {
        return (
            !!this.selectedResultCtrl?.value?.equals(PurchaseRequisitionResult.ORDER) &&
            this.currencyAcceptance?.accepted === false
        );
    }

    /** Selectable currencies as a readable list for the blocker message; derived per lookup. */
    public selectableCurrencyList = '';
    /**
     * True when the customer itself does not allow the demand currency — the blocker message then
     * names the customer instead of blaming the suppliers (EPROC-11425 reopen #2).
     */
    public customerRejectsDemandCurrency = false;
    /**
     * True when the lookup reports zero selectable currencies (empty customer-supplier
     * intersection). Blocks deliberately (decision 06.08.2026) — the blocker message names the
     * way out instead of dangling an empty "selectable" list.
     */
    public noSelectableCurrencies = false;

    public onCurrencyAcceptanceChanged(acceptance: DemandSelectableCurrencies | undefined): void {
        this.currencyAcceptance = acceptance;
        this.selectableCurrencyList = acceptance?.currencies.join(', ') ?? '';
        this.customerRejectsDemandCurrency = !!acceptance?.customerRejects(acceptance.demandCurrency);
        this.noSelectableCurrencies = !!acceptance && acceptance.currencies.length === 0;
        this.cdr.detectChanges();
    }

    public get approvalBlocked(): boolean {
        // EPROC-11425/11560 Konsolidierung: currencyBlocksOrder is the order-only hard block —
        // it only engages while the ORDER result is selected (harter Block nur Bestellweg).
        return !this.purchaseRequisitionValid || !this.resultValid || this.isCompleted || this.currencyBlocksOrder;
    }

    public approve(): void {
        if (!this.approvalBlocked) {
            this._isCompleted = true;
            this.complete(true);
        }
    }

    public decline(): void {
        this.explanationDialog.open(this.explanation, true).subscribe((result: IExplanationDialogResult) => {
            this.explanation = result.explanation;
            if (!result.cancelled) {
                this.complete(false, false, result.explanation);
            }
        });
    }

    public sendToRework(): void {
        this.explanationDialog.open(this.explanation, true).subscribe((result: IExplanationDialogResult) => {
            this.explanation = result.explanation;
            if (!result.cancelled) {
                this.complete(false, true, result.explanation);
            }
        });
    }

    ngOnInit(): void {
        this.taskId = this.taskId ? this.taskId : this.route.snapshot.params['taskId'];
        this.getTask();
    }

    public purchaseRequisitionValidationChange(isValid: boolean): void {
        this.purchaseRequisitionValid = isValid;
        this.cdr.detectChanges();
    }

    public getTask() {
        if (this.taskId) {
            this.taskDataService
                .getSingleTaskById<IPurchaseRequisitionApproveWithResultSelectionTask>(this.taskId)
                .subscribe((task: IPurchaseRequisitionApproveWithResultSelectionTask) => {
                    const typedTask = new PurchaseRequisitionApproveWithResultSelectionTask(
                        task,
                        this.demandDataService,
                        this.commentDataService,
                        this.itemClassificationDataService,
                        this.fileDataService,
                        this.i18n,
                    );
                    this.initResultSelection(typedTask);
                    this.task = typedTask;
                });
        }
    }

    private async complete(isApproved: boolean, rework?: boolean, explanation?: string): Promise<void> {
        if (this.task) {
            await this.task.purchaseRequisition.demand.saveMetaData();
            if (isApproved) {
                const isCompanyCodeValid = await this.validateCompanyCode();
                if (!isCompanyCodeValid) {
                    this._isCompleted = false;
                    return;
                }
            }
            this.taskDataService
                .completeTask(
                    this.task.id,
                    new PurchaseRequisitionApproveWithResultSelectionCompletionData(
                        isApproved,
                        this.selectedResultCtrl!.value?.type,
                        rework,
                        explanation,
                    ),
                )
                .subscribe({
                    next: async () => {
                        this.deeplinkCompletionContext.completeRun(
                            createDeeplinkCompletionContext(
                                isApproved
                                    ? DeeplinkCompletionAction.PURCHASE_REQUISITION_APPROVED
                                    : rework
                                      ? DeeplinkCompletionAction.PURCHASE_REQUISITION_SENT_TO_REWORK
                                      : DeeplinkCompletionAction.PURCHASE_REQUISITION_REJECTED,
                                this.task!.purchaseRequisition.number,
                            ),
                        );
                        this.completed.emit();
                        if (this.routeToListAfterCompletion) {
                            await this.router.navigate([this.router.getViewFromRoute(), 'task', 'list'], {
                                replaceUrl: true,
                            });
                        }
                    },
                    error: (error: HttpErrorResponse) => {
                        this._isCompleted = false;
                        this.applyCompletionValidationError(error);
                    },
                });
        }
    }

    /**
     * A rejected task completion carries the validation error in the 403 body. Demand-level errors
     * are shown inline on the demand; purchase-requisition-level ones (e.g. EPROC-11425: the
     * currency is not accepted by the supplier) used to be dropped silently here, because only the
     * DEMAND subject was handled — the buyer pressed "freigeben" and saw nothing.
     */
    private applyCompletionValidationError(error: HttpErrorResponse): void {
        const validationError: IValidationError | undefined = error?.error?.error;
        if (!validationError || !this.task?.purchaseRequisition) {
            return;
        }

        if (validationError.subject === ValidationSubject.DEMAND) {
            this.task.purchaseRequisition.demand.setValidationError(
                new DemandValidationError(validationError as IDemandValidationError, this.i18n),
            );
            return;
        }

        if (validationError.subject !== ValidationSubject.PURCHASE_REQUISITION) {
            return;
        }

        const purchaseRequisitionError = validationError as IPurchaseRequisitionValidationError;
        if (purchaseRequisitionError.demandError) {
            this.task.purchaseRequisition.demand.setValidationError(
                new DemandValidationError(purchaseRequisitionError.demandError, this.i18n),
            );
        }
        if (purchaseRequisitionError.wrongState) {
            this.notificationService.showNotification(
                purchaseRequisitionError.wrongState,
                NotificationMessageLevel.ERROR,
                undefined,
                true,
            );
        }
    }

    private async validateCompanyCode(): Promise<boolean> {
        if (!this.task?.purchaseRequisition?.id) {
            return true;
        }

        try {
            const response = await firstValueFrom(
                this.purchaseRequisitionDataService.validateCompanyCode(this.task.purchaseRequisition.id),
            );
            return this.handleCompanyCodeValidationResponse(response);
        } catch {
            this.notificationService.showNotification(
                marker('Kontierungsprüfung für den Buchungskreis konnte nicht durchgeführt werden.'),
                NotificationMessageLevel.ERROR,
            );
            return false;
        }
    }

    private handleCompanyCodeValidationResponse(response: IPurchaseRequisitionCompanyCodeValidationResponse): boolean {
        if (response.companyCodeMissing) {
            this.applyCompanyCodeMissingError();
            this.notificationService.showNotification(
                marker('Buchungskreis muss gesetzt sein, bevor die Bedarfsmeldung freigegeben werden kann.'),
                NotificationMessageLevel.ERROR,
            );
            return false;
        }

        if (!response.errors?.length) {
            this.clearCompanyCodeValidationError();
            return true;
        }

        const message = this.buildCompanyCodeValidationMessage(response.errors);
        if (response.blocking) {
            this.applyCompanyCodeValidationError(response.errors);
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR, undefined, true);
            return false;
        }

        this.notificationService.showNotification(message, NotificationMessageLevel.WARNING, undefined, true);
        return true;
    }

    private buildCompanyCodeValidationMessage(errors: IAccountingValidationError[]): string {
        return errors.map((error) => new AccountingValidationError(error, this.i18n).message).join('\n');
    }

    private applyCompanyCodeMissingError(): void {
        if (!this.task?.purchaseRequisition?.demand) {
            return;
        }

        const message = this.i18n.translateInstant(
            marker('Buchungskreis muss gesetzt sein, bevor die Bedarfsmeldung freigegeben werden kann.'),
        );
        const dto: IDemandValidationError = {
            demandId: this.task.purchaseRequisition.demand.id,
            otherErrors: true,
            otherErrorsMessage: message,
            extensionErrors: [],
            subject: ValidationSubject.DEMAND,
        };
        this.task.purchaseRequisition.demand.setValidationError(new DemandValidationError(dto, this.i18n));
        this.hasCompanyCodeValidationError = true;
    }

    private applyCompanyCodeValidationError(errors: IAccountingValidationError[]): void {
        if (!this.task?.purchaseRequisition?.demand) {
            return;
        }

        const dto: IDemandValidationError = {
            demandId: this.task.purchaseRequisition.demand.id,
            otherErrors: false,
            otherErrorsMessage: '',
            extensionErrors: errors,
            subject: ValidationSubject.DEMAND,
        };
        this.task.purchaseRequisition.demand.setValidationError(new DemandValidationError(dto, this.i18n));
        this.hasCompanyCodeValidationError = true;
    }

    private clearCompanyCodeValidationError(): void {
        if (this.hasCompanyCodeValidationError && this.task?.purchaseRequisition?.demand) {
            this.task.purchaseRequisition.demand.validationError = undefined;
            this.hasCompanyCodeValidationError = false;
        }
    }

    private initResultSelection(typedTask: PurchaseRequisitionApproveWithResultSelectionTask) {
        this.selectedResultCtrl = new FormControl<PurchaseRequisitionResultOption>(typedTask.defaultResult, {
            nonNullable: true,
        });
        typedTask.purchaseRequisition.resultsIn = this.selectedResultCtrl.value.type;
        this.selectedResultCtrl.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result: PurchaseRequisitionResultOption) => {
                if (this.task) {
                    this.task.purchaseRequisition.resultsIn = result.type;
                }
            });
    }
}
